import { ScaffoldingFile } from "../ScaffoldingFile";
import { TsImport } from "./TsImport";

export interface TsFile extends ScaffoldingFile {
  imports: TsImport[];
  declarations: string[];
}

export function tsFile(): TsFile {
  return {
    imports: [],
    declarations: [],
    getContent: tsFileContent,
  };
}

interface TsImportStatement {
  moduleName: string;
  namedImports?: Set<string>;
  typedImports?: Set<string>;
  defaultImport?: string;
  namespaceImport?: string;
}

function tsFileContent(this: TsFile): string {
  const importStatements = tsImportsToStatements(this.imports);
  return (
    [
      // Imports
      importStatements
        .sort((i1, i2) => i1.moduleName.localeCompare(i2.moduleName))
        .map((i) => tsImportStatementToString(i))
        .join("\n"),

      // Declarations
      ...this.declarations,
    ]
      .filter((s) => s)
      .join("\n\n") + "\n"
  );
}

function tsImportsToStatements(imports: TsImport[]): TsImportStatement[] {
  const importsStatements = new Map<string, TsImportStatement>();
  for (const imp of imports) {
    let statement = importsStatements.get(imp.moduleName);
    if (!statement) {
      importsStatements.set(
        imp.moduleName,
        (statement = { moduleName: imp.moduleName }),
      );
    }
    if (imp.isDefault) {
      statement.defaultImport = imp.name;
    } else if (imp.isNamespace) {
      statement.namespaceImport = imp.name;
    } else if (imp.isType) {
      (statement.typedImports ??= new Set()).add(imp.name);
    } else if (imp.name) {
      (statement.namedImports ??= new Set()).add(imp.name);
    }
  }
  return Array.from(importsStatements.values());
}

function tsImportStatementToString(importStatement: TsImportStatement): string {
  const namedImportsArr = importStatement.namedImports
    ? Array.from(importStatement.namedImports).sort((i1, i2) =>
        i1.localeCompare(i2),
      )
    : [];
  const typedImportsArr = importStatement.typedImports
    ? Array.from(importStatement.typedImports)
        .sort((i1, i2) => i1.localeCompare(i2))
        .map((i) => `type ${i}`)
    : [];
  const namedTypedImports =
    namedImportsArr.length > 0 || typedImportsArr.length > 0
      ? `{ ${[...namedImportsArr, ...typedImportsArr].join(", ")} }`
      : null;
  const defaultImport = importStatement.defaultImport || null;
  const namespaceImport = importStatement.namespaceImport
    ? `* as ${importStatement.namespaceImport}`
    : null;
  const allImports = [defaultImport, namespaceImport, namedTypedImports]
    .filter((s) => s)
    .join(", ");
  const moduleName = `"${importStatement.moduleName}"`;

  return allImports
    ? `import ${allImports} from ${moduleName};`
    : `import ${moduleName};`;
}
